import { useFormik } from 'formik';
import IBAN from "iban";
import React, { useContext, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { isValidPhoneNumber } from "react-phone-number-input";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Text, cn } from 'rizzui';
import { toast } from 'sonner';
import Spinner from '../../components/bootstrap/Spinner';
import CustomModal from '../../components/modal/CustomModal';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import useHandleErrors from '../../hooks/useHandleErrors';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import Page from '../../layout/Page/Page';
import { AuthState } from '../../redux/authSlice';
import { RootState } from '../../redux/store';
import { ContractService } from '../../services/contract/contractService';
import { Contract } from '../../type/entities/contract-type';
import { isEqual, omitKey } from '../../utils/objectFunctions';
import yup from '../../yupExtensions';
import ClientCommentHistory from './components/ClientCommentsHistory';
import ClientConsultancyHistory from './components/ClientConsultancyHistory';
import ClientContractForm from './components/ClientContractForm';
import ClientContractHistory from './components/ClientContractHistory';
import ClientStudentsHistory from './components/ClientStudentsHistory';
import CommercialTransferModal from './components/CommercialTransferModal';
import ConsultancyContractForm from './components/ConsultancyContractForm';
import ContractDataForm from './components/ContractDataForm';
import ContractFieldsSearch from './components/ContractFieldsSearch';
import ProductContractForm from './components/ProductContractForm';
import StudentContractForm from './components/StudentContractForm';
import { ContractContext } from './providers/ContractProvider';
import { buildOriginalEntityData, createInitialValues, editEntityData, getEntityDifferences, getCursoBonificadoUniqueContactValidationError, SUBSIDISED_PRODUCT_TYPE_ID } from './utils/contractFormUtils';

const CONTRACT_FIELD_LABELS: Record<string, string> = {
    clientId: 'Cliente',
    clientActivity: 'Descripción de la Actividad',
    clientAddress: 'Dirección',
    clientCifNif: 'CIF/NIF',
    clientCity: 'Población',
    clientCnae: 'CNAE',
    clientCompanyCreationDate: 'Creación de la Empresa',
    clientCompanyName: 'Razón Social',
    clientEmail: 'Email',
    clientIban: 'IBAN',
    clientPostalCode: 'Código Postal',
    clientProvince: 'Provincia',
    clientSocialSecurityNumber: 'Nº Seguridad Social',
    clientTelephone: 'Teléfono',
    clientTelephoneSecondary: 'Teléfono adicional 1',
    clientTelephoneThird: 'Teléfono adicional 2',
    clientWorkersNumber: 'Nº trabajadores',
    clientHasRlt: 'Empresa con RLT',
    clientMobile: 'Móvil',
    clientBic: 'Código BIC',
    productTypeId: 'Tipo de producto',
    productId: 'Producto',
    complements: 'Complementos',
    productAmount: 'Importe',
    totalAmount: 'Importe total',
    campaignId: 'Campaña',
    commercialId: 'Comercial',
    commercialNetworkId: 'Red Comercial',
    draftExpirationDate: 'Fecha de Expiración del Borrador',
    productStartDate: 'Fecha Inicio del Curso',
    productEndDate: 'Fecha Fin del Curso',
    organisingCorporationId: 'Empresa Organizadora',
    providingCorporationId: 'Empresa Impartidora',
    platformServiceCorporationId: 'Empresa Plataforma',
    servicesCorporationId: 'Empresa de Servicios',
    groupings: 'Agrupaciones',
    operatorName: 'Nombre del operador',
    tripartiteGrouping: 'Agrupación Fundae',
    userLogin: 'Login del Creador',
    tutorComment: 'Observaciones del Tutor',
    saleComment: 'Observaciones de la Venta',
    activationDate: 'Fecha de Activación',
    socialInsuranceAmount: 'Cuota de Seguridad Social',
    paymentMethodsId: 'Método de Pago',
    paymentInstalmentsNumber: 'Nº Plazos de Pago',
    expirationTypeId: 'Tipo de Vencimiento',
    contractType: 'Tipo de Contrato',
    legalRepresentativeName: 'Nombre y Apellidos (Representante Legal)',
    legalRepresentativeNif: 'NIF/NIE (Representante Legal)',
    legalRepresentativeEmail: 'Correo electrónico (Representante Legal)',
    legalRepresentativePosition: 'Cargo (Representante Legal)',
    legalRepresentativeSchedule: 'Horario de contacto (Representante Legal)',
    studentId: 'Empleado',
    studentNif: 'NIF/NIE (Empleado)',
    studentName: 'Nombre (Empleado)',
    studentFirstName: 'Primer Apellido (Empleado)',
    studentLastName: 'Segundo Apellido (Empleado)',
    studentEmail: 'Email (Empleado)',
    studentTelephone: 'Teléfono (Empleado)',
    studentTelephoneSecondary: 'Teléfono adicional (Empleado)',
    studentMobile: 'Teléfono móvil (Empleado)',
    studentGender: 'Género (Empleado)',
    studentSocialSecurityNumber: 'Nº Seguridad Social (Empleado)',
    studentStudyId: 'Estudios (Empleado)',
    studentProfessionalCategoryId: 'Categoría Profesional (Empleado)',
    studentQuotationGroupId: 'Grupo de Cotización (Empleado)',
    studentFunctionalArea: 'Área Funcional (Empleado)',
    studentAlternativeShippingAddress: 'Dirección de envío alternativa (Empleado)',
    studentContactSchedule: 'Horario de contacto (Empleado)',
    studentBirthDate: 'Fecha de nacimiento (Empleado)',
    studentComment: 'Observaciones (Empleado)',
    consultancyId: 'Consultoría',
    consultancyName: 'Nombre (Consultoría)',
    consultancyEmail: 'Correo Electrónico (Consultoría)',
    consultancyCif: 'CIF (Consultoría)',
    consultancyContact: 'Persona de Contacto (Consultoría)',
    consultancyTelephone: 'Teléfono (Consultoría)',
    consultancyTelephoneSecondary: 'Teléfono Secundario (Consultoría)',
    consultancyDescription: 'Descripción (Consultoría)',
};

const getErrorFieldLabels = (errors: Record<string, unknown>): string[] => {
    const keys = Object.keys(errors);
    return keys.map(key => {
        const baseKey = key.replace(/\.\d+.*$/, '').split('.')[0];
        return CONTRACT_FIELD_LABELS[baseKey] || CONTRACT_FIELD_LABELS[key] || key;
    }).filter((label, i, arr) => arr.indexOf(label) === i);
};

interface ContractFormProps {
    submitContract: Function;
    submitDraft: Function;
    data?: Contract | undefined;
}

export const draftSchema = yup.object().shape({
    productTypeId: yup.string().required('Campo obligatorio'),
    clientCifNif: yup.string().isValidCifNif().notRequired().nullable(),
    commercialNetworkId: yup.string().required('Campo obligatorio'),
    draftExpirationDate: yup.string().notRequired(),
});

export const contractSchema = yup.object().shape({
    //////////////////////////////////////////
    // CLIENT CONTRACT DATA VALIDATION
    //////////////////////////////////////////
    clientId: yup.string().notRequired(),
    clientActivity: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    clientAddress: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    clientCifNif: yup.string().required('Campo obligatorio').isValidCifNif(),
    clientCity: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    clientCnae: yup.string().matches(/^[0-9]{4}$/, 'CNAE no válido').required('Campo obligatorio'),
    clientCompanyCreationDate: yup.string().required('La fecha de creación de la empresa es obligatoria'),
    clientCompanyName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').required('Campo obligatorio'),
    clientEmail: yup.string().email('Correo electrónico no válido').required('Campo obligatorio'),
    clientIban: yup.string().test('iban', 'El formato de IBAN no es válido', (value: any) => IBAN.isValid(value)).nullable(),
    clientPostalCode: yup.string().matches(/^[0-9]{5}$/, 'Código postal no válido').required('Campo obligatorio'),
    clientProvince: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    clientSocialSecurityNumber: yup.string().isValidSS().required('Campo obligatorio'),
    clientTelephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
    clientTelephoneSecondary: yup.string().nullable().notRequired().nullable(),
    clientTelephoneThird: yup.string().nullable().notRequired().nullable(),
    clientWorkersNumber: yup.number().min(1, 'Valor mínimo 1').required('Campo obligatorio'),
    clientHasRlt: yup.boolean().required('Campo obligatorio').test('client-has-rlt', 'Debe indicar si la empresa tiene RLT', (value: any) => value === true || value === false),
    clientMobile: yup.string().required('El móvil es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
    clientBic: yup.string().nullable().test('bic', 'El formato de BIC no es válido', (value: any) => value === undefined || value === '' || /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value)).notRequired().nullable(),

    //////////////////////////////////////////
    // PRODUCT CONTRACT DATA VALIDATION
    //////////////////////////////////////////
    productTypeId: yup.string().required('Campo obligatorio'),
    productId: yup.string().required('Campo obligatorio'),
    complements: yup.array().of(yup.object().shape({
        complementId: yup.string().required('Campo obligatorio'),
        amount: yup.number().required('Campo obligatorio'),
    })),
    productAmount: yup.number().required('Campo obligatorio'),
    totalAmount: yup.number().required('Campo obligatorio'),
    studentFieldsRequired: yup.boolean().notRequired().nullable(),
    consultancyFieldsRequired: yup.boolean().notRequired().nullable(),

    //////////////////////////////////////////
    // CONTRACT DATA VALIDATION (GENERAL INFO)
    //////////////////////////////////////////
    campaignId: yup.string().notRequired().nullable(),
    commercialId: yup.string(),
    commercialNetworkId: yup.string().required('Campo obligatorio'),
    productStartDate: yup.string().notRequired().nullable(),
    productEndDate: yup.string().nullable().notRequired(),
    organisingCorporationId: yup.string().when('productTypeId', {
        is: '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1',
        then: schema => schema.required('Debes seleccionar una empresa organizadora'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    servicesCorporationId: yup.string().when('productTypeId', {
        is: '0196c436-0077-797e-b1ca-7e5be278ad90',
        then: schema => schema.required('Debes seleccionar una empresa servicios'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    providingCorporationId: yup.string().when(['productTypeId', 'servicesCorporationId'], {
        is: (productTypeId: string, servicesCorporationId: string) =>
            productTypeId === '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1' && !servicesCorporationId,
        then: schema => schema.required('Campo obligatorio para productos bonificados'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    platformServiceCorporationId: yup.string().notRequired().nullable(),
    groupings: yup.string().notRequired().nullable(),
    operatorName: yup.string().max(200, 'Demasiado largo').notRequired().nullable(),
    tripartiteGrouping: yup.string().notRequired().nullable(),
    userLogin: yup.string().notRequired().nullable(),
    tutorComment: yup.string().notRequired().nullable(),
    saleComment: yup.string().notRequired().nullable(),
    activationDate: yup.string().required('Campo obligatorio'),
    socialInsuranceAmount: yup.number().notRequired().nullable(),
    paymentMethodsId: yup.string().required('Campo obligatorio'),
    paymentInstalmentsNumber: yup.number().notRequired().nullable().min(1, 'Valor mínimo 1'),
    expirationTypeId: yup.string().notRequired().nullable(),
    contractType: yup.string().oneOf(['recruitment', 'renovation']).notRequired().nullable(),


    //////////////////////////////////////////
    // LEGAL REPRESENTATIVE DATA VALIDATION
    //////////////////////////////////////////
    legalRepresentativeName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').nullable(),
    legalRepresentativeNif: yup.string().isValidNifNIE().nullable(),
    legalRepresentativeEmail: yup.string().email('Email no válido').nullable(),
    legalRepresentativePosition: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').nullable(),
    legalRepresentativeSchedule: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').nullable(),

    //////////////////////////////////////////
    // STUDENT CONTRACT DATA VALIDATION
    //////////////////////////////////////////
    studentId: yup.string().notRequired(),
    studentNif: yup.string().when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio').isValidNifNIE(),
        otherwise: schema => schema.isValidNifNIE().notRequired().nullable(),
    }),
    studentName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentFirstName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentLastName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentEmail: yup.string().email('Correo electrónico no válido').when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentTelephone: yup.string().when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('El teléfono es obligatorio').test("student-valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentTelephoneSecondary: yup.string().nullable().notRequired(),
    studentMobile: yup.string().nullable().notRequired(),
    studentGender: yup.string().notRequired().nullable(),
    studentTerrorismVictim: yup.boolean().notRequired(),
    studentGenderViolenceVictim: yup.boolean().notRequired(),
    studentDisability: yup.boolean().notRequired(),
    studentSocialSecurityNumber: yup.string().isValidSS().nullable(),
    studentStudyId: yup.string().notRequired().nullable(),
    studentProfessionalCategoryId: yup.string().notRequired().nullable(),
    studentQuotationGroupId: yup.string().notRequired().nullable(),
    studentFunctionalArea: yup.string().notRequired().nullable(),
    studentAlternativeShippingAddress: yup.string().nullable(),
    studentContactSchedule: yup.string().nullable(),
    studentBirthDate: yup.string().when('studentFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    studentComment: yup.string().nullable(),

    //////////////////////////////////////////
    // CONSULTANCY CONTRACT DATA VALIDATION
    //////////////////////////////////////////
    consultancyId: yup.string().notRequired().nullable(),
    consultancyName: yup.string().when('consultancyFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    consultancyEmail: yup.string().email('Correo electrónico no válido').when('consultancyFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    consultancyCif: yup.string().isValidCifNif().when('consultancyFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    consultancyContact: yup.string().when('consultancyFieldsRequired', {
        is: true,
        then: schema => schema.required('Campo obligatorio'),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    consultancyTelephone: yup.string().when('consultancyFieldsRequired', {
        is: true,
        then: schema => schema.required('El teléfono es obligatorio').test("consultancy-valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
        otherwise: schema => schema.notRequired().nullable(),
    }),
    consultancyTelephoneSecondary: yup.string().notRequired().nullable(),
    consultancyDescription: yup.string().notRequired().nullable(),
}).test(
    'at-least-one-field',
    'Todos los campos del representante legal deben ser completados si se proporciona alguno',
    function (values) {
        const fields = [
            'legalRepresentativeEmail',
            'legalRepresentativeName',
            'legalRepresentativeNif',
            'legalRepresentativePosition',
            'legalRepresentativeSchedule'
        ];

        const filledFields = fields.filter(field => !!values[field as keyof typeof values]);

        if (filledFields.length > 0 && filledFields.length < fields.length) {
            const emptyFields = fields.filter(field => !values[field as keyof typeof values]);

            return new yup.ValidationError(
                emptyFields.map(field => new yup.ValidationError(
                    'Campo obligatorio',
                    values[field as keyof typeof values],
                    field
                ))
            );
        }
        return true;
    }
).test(
    'curso-bonificado-unique-contacts',
    'Los datos de contacto de empresa y alumno no pueden coincidir en contratos de Curso Bonificado',
    function (values) {
        const validationError = getCursoBonificadoUniqueContactValidationError(values);

        if (validationError) {
            return new yup.ValidationError(
                validationError.message,
                validationError.value,
                validationError.path
            );
        }

        return true;
    }
);

const ContractForm: React.FC<ContractFormProps> = ({ submitContract, submitDraft, data }) => {

    // HOOKS

    const { user }: AuthState = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const {
        setShowClientAsideMenuBtn, isOpenClientHistory, loadingContract, loadingDraft, setLoadingContract,
        setLoadingDraft, originalClientData, originalStudentData, originalConsultancyData,
        setOriginalClientData, setOriginalStudentData, setOriginalConsultancyData,
    } = useContext(ContractContext);
    const mode = data ? 'Editar' : 'Crear';

    // Función createInitialValues ahora importada desde utils

    // STATES

    const [initialValues, setInitialValues] = React.useState<any>(createInitialValues(data, user));
    const [cancelContract, setCancelContract] = React.useState<boolean>(false);
    const [openConfirmClientChanges, setOpenConfirmClientChanges] = React.useState<boolean>(false);
    const [openConfirmStudentChanges, setOpenConfirmStudentChanges] = React.useState<boolean>(false);
    const [openConfirmConsultancyChanges, setOpenConfirmConsultancyChanges] = React.useState<boolean>(false);
    const [openCommercialTransferModal, setOpenCommercialTransferModal] = React.useState<boolean>(false);
    const [commercialTransferData, setCommercialTransferData] = React.useState<{ currentCommercial: string, newCommercial: string }>({ currentCommercial: '', newCommercial: '' });
    const [clientDifferences, setClientDifferences] = React.useState<any>({});
    const [studentDifferences, setStudentDifferences] = React.useState<any>({});
    const [consultancyDifferences, setConsultancyDifferences] = React.useState<any>({});
    const [sendingForm, setSendingForm] = React.useState<boolean>(false);
    const submitValuesRef = React.useRef<any>(initialValues);
    const previousRequiredFlagsRef = React.useRef({
        studentFieldsRequired: initialValues.studentFieldsRequired,
        consultancyFieldsRequired: initialValues.consultancyFieldsRequired
    });

    // FORMIK

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: yup.lazy(({ saveAsContract }) => saveAsContract ? contractSchema : draftSchema),
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { 
            if (loadingContract || loadingDraft || sendingForm) return;
            submitValuesRef.current = values;
            setSendingForm(true);
            submit(values) 
        }
    });

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ENVIAR CONTRATO/BORRADOR 
     * @EN SUBMIT CONTRACT/DRAFT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    // Helper function to check if differences exist
    const hasDifferences = (differences: any) => differences && Object.keys(differences).length > 0;

    /**
     * @ES PROCESAR DIFERENCIAS Y ENVIAR CONTRATO/BORRADOR
     * @EN PROCESS DIFFERENCES AND SUBMIT CONTRACT/DRAFT
     */
    const processDifferencesAndSubmit = async (values: any) => {
        var formatValues = { ...values };
        delete formatValues.totalAmount;

        // Check for differences and show modals if needed
        if (formatValues.saveAsContract) {
            if (hasDifferences(getEntityDifferences(formatValues, originalClientData, 'client', userCan, setClientDifferences))) setOpenConfirmClientChanges(true);
            if (hasDifferences(getEntityDifferences(formatValues, originalStudentData, 'student', userCan, setStudentDifferences))) setOpenConfirmStudentChanges(true);
            if (hasDifferences(getEntityDifferences(formatValues, originalConsultancyData, 'consultancy', userCan, setConsultancyDifferences))) setOpenConfirmConsultancyChanges(true);
        }

        // Submit contract or draft only if there are no differences
        const noDifferences =
            !hasDifferences(getEntityDifferences(formatValues, originalClientData, 'client', userCan, setClientDifferences)) &&
            !hasDifferences(getEntityDifferences(formatValues, originalStudentData, 'student', userCan, setStudentDifferences)) &&
            !hasDifferences(getEntityDifferences(formatValues, originalConsultancyData, 'consultancy', userCan, setConsultancyDifferences));

        if (noDifferences) {
            if (formatValues.saveAsContract) {
                submitContract(formatValues).then(() => {
                    if (formatValues.stayOnPage) {
                        setLoadingContract(false);
                        toast.success('Contrato guardado correctamente');
                    }
                });
            }
            if (formatValues.saveAsDraft) {
                submitDraft(formatValues).then(() => {
                    if (formatValues.stayOnPage) {
                        setLoadingDraft(false);
                        toast.success('Borrador guardado correctamente');
                    }
                });
            }
        }
    };

    /**
     * @ES MANEJAR ENVÍO DEL FORMULARIO (BORRADOR O CONTRATO)
     * @EN HANDLE FORM SUBMISSION (DRAFT OR CONTRACT)
     * Misma lógica para ambos: validar, actualizar valores y enviar. La diferencia es solo saveAsContract/saveAsDraft.
     */
    const handleSubmitButton = async (options: { saveAsContract: boolean; saveAsDraft: boolean; stayOnPage: boolean }) => {
        if (loadingContract || loadingDraft || sendingForm) return;
        const valuesToSubmit = { ...formik.values, ...options };
        submitValuesRef.current = valuesToSubmit;
        const schemaToUse = options.saveAsDraft ? draftSchema : contractSchema;
        let errors: Record<string, string> = {};
        try {
            await schemaToUse.validate(valuesToSubmit, { abortEarly: false });
        } catch (err: any) {
            const studentFields = ['studentName', 'studentFirstName', 'studentLastName', 'studentNif', 'studentEmail', 'studentTelephone', 'studentMobile', 'studentBirthDate'];
            const consultancyFields = ['consultancyName', 'consultancyEmail', 'consultancyCif', 'consultancyContact', 'consultancyTelephone'];
            if (err?.inner) {
                err.inner.forEach((e: { path?: string; message?: string }) => {
                    if (e.path) {
                        // No exigir campos de empleado/asesoría si el formulario no los muestra (no se pueden rellenar)
                        if (!valuesToSubmit.studentFieldsRequired && studentFields.includes(e.path)) return;
                        if (!valuesToSubmit.consultancyFieldsRequired && consultancyFields.includes(e.path)) return;
                        errors[e.path] = e.message || '';
                    }
                });
            } else if (err?.path && err?.message) {
                const skipStudent = !valuesToSubmit.studentFieldsRequired && studentFields.includes(err.path);
                const skipConsultancy = !valuesToSubmit.consultancyFieldsRequired && consultancyFields.includes(err.path);
                if (!skipStudent && !skipConsultancy) errors[err.path] = err.message;
            }
        }
        if (Object.keys(errors).length > 0) {
            formik.setErrors(errors);
            formik.setTouched(Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
            const fieldLabels = getErrorFieldLabels(errors);
            const fieldsText = fieldLabels.length > 0 ? ` Campos: ${fieldLabels.join(', ')}.` : '';
            toast.error(`Hay errores en el formulario.${fieldsText} Revisa los campos marcados en rojo.`);
            const firstErrorKey = Object.keys(errors)[0];
            const element = document.getElementById(firstErrorKey) || document.querySelector(`[name="${firstErrorKey}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            formik.setValues(valuesToSubmit);
            setSendingForm(true);
            submit(valuesToSubmit);
        }
    };

    const submit = async (values: any) => {
        // First: Check if commercial has changed when editing a contract
        if (mode === 'Editar' && values.saveAsContract && data?.id && formik.values.commercialId) {
            try {
                const response = await (await (new ContractService()).checkCurrentCommercialHistoryPoint(data.id, formik.values.commercialId))?.getResponseData();
                if (response.success) {
                    if (!response?.data?.isSameCommercial) {
                        // Set commercial transfer data and show modal
                        setCommercialTransferData({
                            currentCommercial: response?.data?.currentCommercialName || 'Comercial anterior',
                            newCommercial: commercialTransferData.newCommercial || 'Nuevo comercial'
                        });
                        setOpenCommercialTransferModal(true);
                        return; // Stop submission to show modal
                    }
                } else {
                    toast.error(response.message || 'Error al verificar el historial comercial');
                }
            } catch (error) {
                console.error('Error checking commercial history:', error);
                toast.error('Error al verificar el historial comercial');
                return; // Stop submission on error
            }
        }

        // Second: Process differences and submit
        await processDifferencesAndSubmit(values);
        await setSendingForm(false);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR TRANSFERENCIA DE PUNTOS COMERCIALES
     * @EN HANDLE COMMERCIAL POINTS TRANSFER
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleTransferPoints = async () => {
        try {
            const response = (await (new ContractService()).transferPointsToNewCommercial(data?.id || '', formik.values.commercialId)).getResponseData();
            if (response.success) {
                toast.success('Puntos transferidos correctamente');
                setOpenCommercialTransferModal(false);
            } else {
                toast.error('Error al transferir los puntos');
            }
        } catch (error) {
            console.error('Error transferring points:', error);
            toast.error('Error al transferir los puntos');
        } finally {
            // Continue with the rest of the submit process after the transfer
            continueSubmitAfterTransfer(formik.values);
        }
    };

    /**
     * @ES CONTINUAR CON EL PROCESO DE SUBMIT DESPUÉS DE LA TRANSFERENCIA
     * @EN CONTINUE WITH SUBMIT PROCESS AFTER TRANSFER
     */
    const continueSubmitAfterTransfer = async (values: any) => {
        await processDifferencesAndSubmit(values);
    };

    const handleCancelTransfer = () => {
        setOpenCommercialTransferModal(false);
        toast.info('Transferencia de puntos cancelada');
        // Continue with the rest of the submit process after the transfer
        continueSubmitAfterTransfer(formik.values);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // Función genérica getDifferencesWithPermission movida a utils
    //-------------------------------------------------------------------------------------------------------------------------------

    // Función genérica getEntityDifferences importada desde utils
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------

    // Función genérica editEntityData importada desde utils
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    /**
     * Set initial values when data is loaded (edit mode)
     */
    useEffect(() => {
        if (data) {
            const nextInitialValues = createInitialValues(data, user);
            setInitialValues(nextInitialValues);
            setOriginalClientData(buildOriginalEntityData(nextInitialValues, 'client'));
            setOriginalStudentData(buildOriginalEntityData(nextInitialValues, 'student'));
            setOriginalConsultancyData(buildOriginalEntityData(nextInitialValues, 'consultancy'));
        } else {
            setOriginalClientData(undefined);
            setOriginalStudentData(undefined);
            setOriginalConsultancyData(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, user]);

    /**
     * Show aside menu button when client is selected
     */
    useEffect(() => {
        if (formik.values.clientId?.length >= 32 || (data && data?.client?.id?.length >= 32)) {
            setShowClientAsideMenuBtn(true);
        }
        return () => setShowClientAsideMenuBtn(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.clientId, data]);

    /**
     * Prevent user from leaving the page if there are unsaved changes in the form
     */
    useEffect(() => {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            if ((mode === 'Crear' && formik.dirty) || (mode === 'Editar' && !isEqual(omitKey(initialValues, "legalRepresentativeExist"), omitKey(formik.values, "legalRepresentativeExist")))) {
                e.preventDefault();
                e.returnValue = 'Si abandonas la página perderás los datos introducidos en el contrato';
            }
        };
        return () => window.onbeforeunload = null as any;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.dirty, mode, formik.values]);

    /**
     * Reset student and consultancy fields when the corresponding fields are not required
     */
    useEffect(() => {
        const clearStudentFields = () => {
            formik.setFieldValue('studentId', '');
            formik.setFieldValue('studentNif', '');
            formik.setFieldValue('studentName', '');
            formik.setFieldValue('studentFirstName', '');
            formik.setFieldValue('studentLastName', '');
            formik.setFieldValue('studentEmail', '');
            formik.setFieldValue('studentTelephone', '');
            formik.setFieldValue('studentTelephoneSecondary', '');
            formik.setFieldValue('studentGender', '');
            formik.setFieldValue('studentTerrorismVictim', false);
            formik.setFieldValue('studentGenderViolenceVictim', false);
            formik.setFieldValue('studentDisability', false);
            formik.setFieldValue('studentSocialSecurityNumber', '');
            formik.setFieldValue('studentStudyId', null);
            formik.setFieldValue('studentProfessionalCategoryId', null);
            formik.setFieldValue('studentQuotationGroupId', null);
            formik.setFieldValue('studentFunctionalArea', '');
            formik.setFieldValue('studentAlternativeShippingAddress', '');
            formik.setFieldValue('studentContactSchedule', '');
            formik.setFieldValue('studentBirthDate', null);
            formik.setFieldValue('studentMobile', '');
            formik.setFieldValue('studentComment', '');
        };

        const clearConsultancyFields = () => {
            formik.setFieldValue('consultancyId', '');
            formik.setFieldValue('consultancyName', '');
            formik.setFieldValue('consultancyEmail', '');
            formik.setFieldValue('consultancyCif', '');
            formik.setFieldValue('consultancyContact', '');
            formik.setFieldValue('consultancyTelephone', '');
            formik.setFieldValue('consultancyTelephoneSecondary', '');
            formik.setFieldValue('consultancyDescription', '');
        };

        if (previousRequiredFlagsRef.current.studentFieldsRequired && !formik.values.studentFieldsRequired) {
            clearStudentFields();
        }

        if (previousRequiredFlagsRef.current.consultancyFieldsRequired && !formik.values.consultancyFieldsRequired) {
            clearConsultancyFields();
        }

        previousRequiredFlagsRef.current = {
            studentFieldsRequired: formik.values.studentFieldsRequired,
            consultancyFieldsRequired: formik.values.consultancyFieldsRequired
        };
    }, [formik.values.studentFieldsRequired, formik.values.consultancyFieldsRequired]);

    // RENDER

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit} className='border-transparent'>
                        <ContractFieldsSearch targetId="contract-form-fields" className="mb-4" />
                        <div className="flex flex-col lg:flex-row flex-nowrap gap-5 mb-2">
                            <div id="contract-form-fields" className={cn(
                                'flex flex-col gap-7 w-full transition-all duration-1000 ease-in-out md:h-[calc(100vh-320px)] md:overflow-y-scroll md:relative md:pe-2 scrollbar',
                                (formik.values.clientId && isOpenClientHistory) && 'lg:w-[70%]'
                            )}>
                                <ClientContractForm formik={formik} />
                                <ProductContractForm formik={formik} />
                                <ContractDataForm formik={formik} setCommercialTransferData={setCommercialTransferData} isCreateMode={mode === 'Crear'} />
                                {formik.values.studentFieldsRequired && <StudentContractForm formik={formik} />}
                                {formik.values.consultancyFieldsRequired && <ConsultancyContractForm formik={formik} />}
                            </div>

                            <div className={`slide-container ${formik.values.clientId && isOpenClientHistory ? 'slide-enter' : 'slide-exit'} `}>
                                <div className={`content-wrapper ${formik.values.clientId && isOpenClientHistory ? 'content-visible' : 'content-hidden'}`}>
                                    {formik.values.clientId && (
                                        <div className="rounded-lg border-solid border-2 col-span-3 p-4 w-full h-fit divide-y-4 divide-dotted md:h-[calc(100vh-330px)] md:overflow-y-scroll md:relative scrollbar">
                                            <ClientCommentHistory clientId={formik.values.clientId} formik={formik} />
                                            <ClientContractHistory clientId={formik.values.clientId} formik={formik} />
                                            {formik.values.studentFieldsRequired && <ClientStudentsHistory clientId={formik.values.clientId} formik={formik} />}
                                            {formik.values.consultancyFieldsRequired && <ClientConsultancyHistory clientId={formik.values.clientId} formik={formik} />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </>
        );
    };

    return (
        <>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>

            <div className='sticky bottom-0 z-10 md:z-0 md:flex md:flex-row md:justify-end grid grid-cols-3 items-center gap-4 border-t bg-white px-4 pt-4 dark:bg-gray-50 -me-4 -ms-4'>
                <Button
                    variant="outline"
                    onClick={() => {
                        if ((mode === 'Crear' && formik.dirty) || (mode === 'Editar' && (JSON.stringify(omitKey(initialValues, "legalRepresentativeExist")) !== JSON.stringify(omitKey(formik.values, "legalRepresentativeExist"))))) {
                            setCancelContract(true);
                            return;
                        }
                        navigate(-1);
                    }}
                    disabled={loadingContract || loadingDraft || sendingForm}
                >
                    {width > 768 ? 'Cancelar' : <IoClose size={20} />}
                </Button>
                <Button
                    name='save-draft-button'
                    type="button"
                    className={cn(data?.isDraft === false && 'hidden')}
                    color="secondary"
                    onClick={(e) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        handleSubmitButton({ saveAsContract: false, saveAsDraft: true, stayOnPage: false });
                    }}
                    isLoading={loadingDraft || sendingForm}
                    disabled={loadingContract || loadingDraft || sendingForm}
                >
                    {loadingDraft ? <Spinner /> : mode + ' Borrador'}
                </Button>
                <Button
                    name='save-contract-button'
                    type="button"
                    onClick={() => handleSubmitButton({ saveAsContract: true, saveAsDraft: false, stayOnPage: false })}
                    isLoading={loadingContract || sendingForm}
                    disabled={loadingContract || loadingDraft || sendingForm}
                >
                    {loadingContract ? <Spinner /> : ((mode === "Editar" && data?.isDraft === true) ? 'Guardar Contrato' : mode + ' Contrato')}
                </Button>

                <Button
                    name='save-and-stay-button'
                    type="button"
                    color="primary"
                    onClick={() => handleSubmitButton({ saveAsContract: true, saveAsDraft: false, stayOnPage: true })}
                    isLoading={loadingContract || sendingForm}
                    disabled={loadingContract || loadingDraft || sendingForm}
                >
                    {loadingContract ? <Spinner /> : 'Guardar y continuar'}
                </Button>
            </div>

            <CustomModal
                name='Datos de contrato'
                isOpen={cancelContract}
                onConfirm={() => { setCancelContract(false); navigate(-1); }}
                onClose={() => setCancelContract(false)}
                confirmText='Sí, salir'
                cancelText='Cancelar'
            >
                <Text className='mb-7'>Se perderán los datos introducidos en el contrato. ¿Estás seguro de que quieres salir?</Text>
            </CustomModal>

            <CustomModal
                name='Datos de cliente'
                isOpen={openConfirmClientChanges && userCan('edit_client_on_contract', 'contracts')}
                onConfirm={() => {
                    setOpenConfirmClientChanges(false);
                    editEntityData(clientDifferences, 'client', new ContractService(), submitValuesRef.current, setLoadingContract, setLoadingDraft, submitContract, submitDraft, handleErrors, toast, { clientConfirm: false, studentConfirm: openConfirmStudentChanges, consultancyConfirm: openConfirmConsultancyChanges });
                }}
                onClose={() => {
                    setOpenConfirmClientChanges(false);
                    if (!openConfirmStudentChanges && !openConfirmConsultancyChanges) {
                        if (submitValuesRef.current.saveAsContract) {
                            submitContract(submitValuesRef.current);
                        } else {
                            submitDraft(submitValuesRef.current);
                        }
                    }
                }}
            >
                <Text className='mb-7'>Se han modificado los datos del cliente. ¿Quieres actualizar los datos en el cliente?</Text>
            </CustomModal>

            <CustomModal
                name='Datos de empleado'
                isOpen={openConfirmStudentChanges && userCan('edit_student_on_contract', 'contracts') && !openConfirmClientChanges}
                onConfirm={() => {
                    setOpenConfirmStudentChanges(false);
                    editEntityData(studentDifferences, 'student', new ContractService(), submitValuesRef.current, setLoadingContract, setLoadingDraft, submitContract, submitDraft, handleErrors, toast, { clientConfirm: openConfirmClientChanges, studentConfirm: false, consultancyConfirm: openConfirmConsultancyChanges });
                }}
                onClose={() => {
                    setOpenConfirmStudentChanges(false);
                    if (!openConfirmClientChanges && !openConfirmConsultancyChanges) {
                        if (submitValuesRef.current.saveAsContract) {
                            submitContract(submitValuesRef.current);
                        } else {
                            submitDraft(submitValuesRef.current);
                        }
                    }
                }}
            >
                <Text className='mb-7'>Se han modificado los datos del empleado. ¿Quieres actualizar los datos en el empleado?</Text>
            </CustomModal>

            <CustomModal
                name='Datos de asesoría'
                isOpen={openConfirmConsultancyChanges && userCan('edit_consultancy_on_contract', 'contracts')}
                onConfirm={() => {
                    setOpenConfirmConsultancyChanges(false);
                    editEntityData(consultancyDifferences, 'consultancy', new ContractService(), submitValuesRef.current, setLoadingContract, setLoadingDraft, submitContract, submitDraft, handleErrors, toast, { clientConfirm: openConfirmClientChanges, studentConfirm: openConfirmStudentChanges, consultancyConfirm: false });
                }}
                onClose={() => {
                    setOpenConfirmConsultancyChanges(false);
                    if (!openConfirmClientChanges && !openConfirmStudentChanges) {
                        if (submitValuesRef.current.saveAsContract) {
                            submitContract(submitValuesRef.current);
                        } else {
                            submitDraft(submitValuesRef.current);
                        }
                    }
                }}
            >
                <Text className='mb-7'>Se han modificado los datos de la asesoría. ¿Quieres actualizar los datos en la asesoría?</Text>
            </CustomModal>

            <CommercialTransferModal
                isOpen={openCommercialTransferModal}
                onClose={handleCancelTransfer}
                onTransfer={handleTransferPoints}
                onCancel={handleCancelTransfer}
                currentCommercialName={commercialTransferData.currentCommercial}
                newCommercialName={commercialTransferData.newCommercial}
            />
        </>
    );
};

export default ContractForm;
